// src/components/PWAManager.jsx
import { useState, useEffect, useCallback } from 'react';

// ── Service Worker 등록 ───────────────────────────────────────

function useServiceWorker() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const newWorkerRef = { current: null };

  useEffect(() => {
    if (!('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') return;

    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then(reg => {
        reg.addEventListener('updatefound', () => {
          const nw = reg.installing;
          newWorkerRef.current = nw;
          nw.addEventListener('statechange', () => {
            if (nw.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateAvailable(true);
            }
          });
        });
        setInterval(() => reg.update(), 60 * 60 * 1000);
      })
      .catch(() => {});

    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyUpdate = useCallback(() => {
    newWorkerRef.current?.postMessage({ type: 'SKIP_WAITING' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { updateAvailable, applyUpdate };
}

// ── 홈 화면 추가 프롬프트 ────────────────────────────────────────

function useInstallPrompt() {
  const [canInstall,  setCanInstall]  = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const promptRef = { current: null };

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }
    const onBefore = e => { e.preventDefault(); promptRef.current = e; setCanInstall(true); };
    const onInstalled = () => { setIsInstalled(true); setCanInstall(false); };
    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const promptInstall = useCallback(async () => {
    if (!promptRef.current) return false; // eslint-disable-line react-hooks/exhaustive-deps
    promptRef.current.prompt();
    const { outcome } = await promptRef.current.userChoice;
    promptRef.current = null;
    setCanInstall(false);
    return outcome === 'accepted';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { canInstall, isInstalled, promptInstall };
}

// ── 온라인/오프라인 ──────────────────────────────────────────────

function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return isOnline;
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────

export default function PWAManager() {
  const { updateAvailable, applyUpdate } = useServiceWorker();
  const { canInstall, isInstalled, promptInstall } = useInstallPrompt();
  const isOnline = useOnlineStatus();

  const [showInstall,  setShowInstall]  = useState(false);
  const [showOffline,  setShowOffline]  = useState(false);
  const [showUpdate,   setShowUpdate]   = useState(false);
  const [installing,   setInstalling]   = useState(false);
  const [onlineBack,   setOnlineBack]   = useState(false);

  // 설치 배너 — 30초 후, 7일 억제
  useEffect(() => {
    if (!canInstall || isInstalled) return;
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - Number(dismissed) < 7 * 86400_000) return;
    const t = setTimeout(() => setShowInstall(true), 30_000);
    return () => clearTimeout(t);
  }, [canInstall, isInstalled]);

  // 오프라인 바
  useEffect(() => {
    if (!isOnline) {
      setShowOffline(true);
      setOnlineBack(false);
    } else if (showOffline) {
      setOnlineBack(true);
      const t = setTimeout(() => { setShowOffline(false); setOnlineBack(false); }, 2500);
      return () => clearTimeout(t);
    }
  }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  // 업데이트 토스트
  useEffect(() => {
    if (updateAvailable) setShowUpdate(true);
  }, [updateAvailable]);

  const handleInstall = async () => {
    setInstalling(true);
    await promptInstall();
    setInstalling(false);
    setShowInstall(false);
  };

  const dismissInstall = () => {
    setShowInstall(false);
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
  };

  return (
    <>
      <style>{`
        @keyframes slideDown2 { from{transform:translateX(-50%) translateY(-100%)} to{transform:translateX(-50%) translateY(0)} }
        @keyframes slideUp2   { from{transform:translateX(-50%) translateY(20px);opacity:0} to{transform:translateX(-50%) translateY(0);opacity:1} }
      `}</style>

      {/* 오프라인 / 복구 바 */}
      {showOffline && (
        <div style={{
          position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '100%', maxWidth: 390, zIndex: 9999,
          background: onlineBack ? '#2e7d32' : '#b71c1c',
          color: '#fff', textAlign: 'center', padding: '9px 16px',
          fontSize: 13, fontWeight: 700,
          animation: 'slideDown2 0.3s ease',
        }}>
          {onlineBack ? '✅ 인터넷 연결이 복구되었습니다' : '📵 인터넷 연결이 없습니다'}
        </div>
      )}

      {/* 업데이트 토스트 */}
      {showUpdate && (
        <div style={{
          position: 'fixed', bottom: 90, left: '50%',
          transform: 'translateX(-50%)',
          width: 'calc(100% - 32px)', maxWidth: 358, zIndex: 9998,
          background: '#1a1a2e', borderRadius: 16, padding: '14px 16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          display: 'flex', alignItems: 'center', gap: 12,
          animation: 'slideUp2 0.3s ease',
        }}>
          <span style={{ fontSize: 22 }}>🔄</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>새 버전이 준비됐습니다</div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }}>업데이트하면 최신 기능을 사용할 수 있습니다</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowUpdate(false)} style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8,
              color: '#fff', padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
            }}>나중에</button>
            <button onClick={() => { applyUpdate(); setShowUpdate(false); }} style={{
              background: '#FFD60A', border: 'none', borderRadius: 8,
              color: '#1a1a2e', padding: '6px 12px', fontSize: 12,
              fontWeight: 900, cursor: 'pointer', fontFamily: 'inherit',
            }}>업데이트</button>
          </div>
        </div>
      )}

      {/* 홈 화면 추가 배너 */}
      {showInstall && !isInstalled && (
        <div style={{
          position: 'fixed', bottom: 90, left: '50%',
          transform: 'translateX(-50%)',
          width: 'calc(100% - 32px)', maxWidth: 358, zIndex: 9997,
          background: '#fff', borderRadius: 20, padding: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
          animation: 'slideUp2 0.3s ease',
        }}>
          <button onClick={dismissInstall} style={{
            position: 'absolute', top: 12, right: 14,
            background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#aaa',
          }}>✕</button>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 12,
              background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
            }}>💼</div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 15, color: '#1a1a2e' }}>하루일거리</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>홈 화면에 추가하면 앱처럼 사용 가능</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={dismissInstall} style={{
              flex: 1, background: '#f0f0f0', border: 'none', borderRadius: 12,
              padding: '11px', fontSize: 14, fontWeight: 700, color: '#666',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>나중에</button>
            <button onClick={handleInstall} disabled={installing} style={{
              flex: 2, background: '#1a1a2e', border: 'none', borderRadius: 12,
              padding: '11px', fontSize: 14, fontWeight: 900,
              color: '#FFD60A', cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {installing ? '설치 중...' : '📲 홈 화면에 추가'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
